// src/api/axios.js
import axios from 'axios';
import { toast } from 'react-toastify';

/* -------- env debug toggle (non-breaking) -------- */
const AXIOS_DEBUG = process.env.REACT_APP_AXIOS_DEBUG === 'true';

/* -------- base URL resolution (works in dev & prod) -------- */
const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||           // Cloudflare Pages (recommended)
  process.env.REACT_APP_API_BASE ||               // legacy/compat var
  (process.env.NODE_ENV === 'production'
    ? 'https://api.asteriq.app'                   // safe prod fallback
    : 'http://localhost:5000/api');               // dev fallback

/* -------- auth mode toggle (Bearer in dev, Cookies in prod) -------- */
const USE_BEARER = process.env.REACT_APP_USE_BEARER === 'true';

if (AXIOS_DEBUG) {
  // eslint-disable-next-line no-console
  console.debug(
    '[axios] baseURL:',
    API_BASE,
    '| useBearer:',
    USE_BEARER,
    '| env:',
    process.env.NODE_ENV
  );
}

/* -------- tiny helpers -------- */
const JUST_LOGGED_OUT_FLAG = 'auth:justLoggedOut';

function isJustLoggedOut() {
  try {
    return sessionStorage.getItem(JUST_LOGGED_OUT_FLAG) === '1';
  } catch {
    return false;
  }
}

function clearJustLoggedOutFlag() {
  try {
    sessionStorage.removeItem(JUST_LOGGED_OUT_FLAG);
  } catch {
    // no-op
  }
}

function isAuthBootProbe(url, base) {
  try {
    const full = new URL(url, base || window.location.origin);
    // Matches /auth/profile or /auth/refresh (future-proof)
    return /\/auth\/(profile|refresh)(\/|$)/i.test(full.pathname);
  } catch {
    return false;
  }
}

/* -------- normalizer -------- */
function normalizeToArray(payload, keyCandidates = ['items', 'data', 'results']) {
  try {
    if (Array.isArray(payload)) return payload;
    for (const k of keyCandidates) {
      const v = payload?.[k];
      if (Array.isArray(v)) return v;
    }
    if (
      payload &&
      typeof payload === 'object' &&
      (payload._id || payload.id || payload.invoiceNumber)
    ) {
      return payload; // detail stays object
    }
  } catch {
    // no-op
  }
  return [];
}

/* -------- meta extractor (preserve pagination/counts) -------- */
function extractMeta(payload) {
  const meta = {};
  try {
    // Helper that reads from top-level, meta, pagination, or double-wrapped data.meta
    const from = (k) =>
      payload?.[k] ??
      payload?.meta?.[k] ??
      payload?.pagination?.[k] ??
      payload?.data?.meta?.[k];

    const candidates = [
      'total',
      'count',
      'page',
      'limit',
      'perPage',
      'pages',
      'totalPages',
      'next',
      'prev',
      'hasMore',
    ];

    for (const k of candidates) {
      const v = from(k);
      if (v !== undefined) meta[k] = v;
    }

    // Map alternates → canonical
    if (meta.total === undefined && meta.count !== undefined) meta.total = meta.count;
    if (meta.limit === undefined && meta.perPage !== undefined)
      meta.limit = meta.perPage;
    if (meta.totalPages === undefined && meta.pages !== undefined)
      meta.totalPages = meta.pages;

    // If still no total, infer from any array we can find
    if (meta.total === undefined) {
      const arr = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.results)
        ? payload.results
        : Array.isArray(payload?.data)
        ? payload.data
        : undefined;
      if (arr) meta.total = arr.length;
    }

    // Sensible defaults
    if (meta.page === undefined) meta.page = 1;
    if (meta.limit === undefined) {
      const guessed = Array.isArray(payload?.data)
        ? payload.data.length
        : Array.isArray(payload?.items)
        ? payload.items.length
        : Array.isArray(payload?.results)
        ? payload.results.length
        : Array.isArray(payload)
        ? payload.length
        : 25;
      meta.limit = guessed || 25;
    }

    // Derive totalPages if missing
    const totalNum = Number(meta.total);
    const limitNum = Number(meta.limit);
    if (
      meta.totalPages === undefined &&
      Number.isFinite(totalNum) &&
      Number.isFinite(limitNum) &&
      limitNum > 0
    ) {
      meta.totalPages = Math.ceil(totalNum / limitNum);
    }
  } catch {
    // leave meta as-is
  }
  return meta;
}

/* -------- axios instance -------- */
const instance = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true, // send cookies for auth; server will ignore if not used
  timeout: Number(process.env.REACT_APP_AXIOS_TIMEOUT_MS || 15000),
});

/* -------- request interceptor -------- */
instance.interceptors.request.use(
  (config) => {
    // Prevent immediate session "resurrection" after logout:
    // If the page just logged out, skip sending cookies for the very next /auth/profile (or /auth/refresh) probe.
    if (
      isJustLoggedOut() &&
      isAuthBootProbe(config.url, instance.defaults.baseURL)
    ) {
      if (AXIOS_DEBUG)
        console.debug(
          '[axios] just logged out → skip credentials for auth boot probe',
          config.url
        );
      config.withCredentials = false; // don't send HttpOnly cookie
      config.headers['X-Skip-Auth'] = '1'; // optional hint for backend/logs
      clearJustLoggedOutFlag(); // ensure this branch runs only once
    }

    if (USE_BEARER) {
      const token = localStorage.getItem('token');
      if (token) config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/* -------- collection adapters (UNCHANGED sets) -------- */
const collectionAdapters = [
  { pattern: /^\/invoices$/, keys: ['items', 'data', 'results', 'invoices'] },
  { pattern: /^\/clients$/, keys: ['items', 'data', 'results', 'clients'] },
  { pattern: /^\/tasks$/, keys: ['items', 'data', 'results', 'tasks'] },
  { pattern: /^\/payments$/, keys: ['items', 'data', 'results', 'payments'] },
  { pattern: /^\/works$/, keys: ['items', 'data', 'results', 'works'] },
];

/* -------- response interceptor (success path) -------- */
instance.interceptors.response.use(
  (response) => {
    try {
      const method = (response.config?.method || 'get').toLowerCase();
      if (method !== 'get') return response;

      const respType = response.config?.responseType;
      const contentType = response.headers?.['content-type'] || '';
      const isJson = /\bapplication\/json\b|\+json\b/i.test(contentType);
      if (respType === 'blob' || (!isJson && contentType)) {
        if (AXIOS_DEBUG)
          console.debug('[axios] skip normalization (non-JSON/blob)', {
            url: response.config?.url,
            respType,
            contentType,
          });
        return response;
      }

      const base = instance.defaults.baseURL || window.location.origin;
      const full = new URL(response.config.url, base);
      const pathname = full.pathname.replace(/\/+$/, '');

      const basePath = new URL(base).pathname.replace(/\/+$/, '');
      const relative = pathname.startsWith(basePath)
        ? pathname.slice(basePath.length) || '/'
        : pathname;

      const adapter = collectionAdapters.find(({ pattern }) =>
        pattern.test(relative)
      );
      if (adapter) {
        const original = response.data;
        const normalized = normalizeToArray(original, adapter.keys);
        if (Array.isArray(normalized)) {
          const meta = extractMeta(original);
          response._meta = meta; // keep for debugging
          response.meta = meta; // <-- expose for consumers like parseList()
          response.data = normalized;
          if (AXIOS_DEBUG) {
            console.debug('[axios] normalized collection', {
              route: relative,
              usedKeys: adapter.keys,
              count: normalized.length,
              meta: response._meta,
            });
          }
        } else if (AXIOS_DEBUG) {
          console.debug(
            '[axios] adapter matched but detail/object detected — no normalization',
            {
              route: relative,
              usedKeys: adapter.keys,
            }
          );
        }
      }
    } catch (e) {
      if (AXIOS_DEBUG) console.debug('[axios] normalization error (ignored)', e);
    }
    return response;
  },

  /* -------- error shaping + subscription guard -------- */
  (error) => {
    try {
      const resp = error?.response;
      const status = resp?.status;
      const headers = resp?.headers || {};
      const data = resp?.data;

      // shape meta flags
      error._status = status;
      error._data = data;
      error._network = !resp; // network / CORS / timeout
      error._is429 = status === 429;
      error._is422 = status === 422;
      error._auth = status === 401 || status === 419;
      error._retryAfter = headers['retry-after']
        ? Number(headers['retry-after'])
        : undefined;

      if (!error.message && typeof data?.message === 'string') {
        error.message = data.message;
      }

      // 🔐 Global subscription guard — backend 402 → toast + redirect
      if (
        status === 402 &&
        data &&
        data.error === 'SUBSCRIPTION_INACTIVE' &&
        !error._subscriptionGuardHandled
      ) {
        error._subscriptionGuardHandled = true;
        const msg =
          data.message ||
          'Your subscription is not active. Please update billing to continue.';

        try {
          toast.error(msg);
        } catch {
          // ignore toast failures
        }

        try {
          if (typeof window !== 'undefined') {
            const path = window.location?.pathname || '';
            // Only redirect if not already on Settings to avoid loops
            if (!path.startsWith('/settings')) {
              window.location.href = '/settings';
            }
          }
        } catch {
          // ignore redirect failures
        }
      }

      if (AXIOS_DEBUG) {
        // eslint-disable-next-line no-console
        console.debug('[axios:error]', {
          url: error?.config?.url,
          method: error?.config?.method,
          status,
          auth: error._auth,
          is429: error._is429,
          is422: error._is422,
          retryAfter: error._retryAfter,
          network: error._network,
        });
      }
    } catch {
      // no-op
    }
    return Promise.reject(error);
  }
);

export default instance;
